/**
 * LiangFanLogo — the brand mark.
 *
 * An emerald hand-fan with a warm amber glow behind it. Represents
 * Liang's pedagogy: patient unfolding (the fan veins) + warm
 * encouragement (the glow). From the Liang Design System brand pack.
 *
 * Two variants:
 *   - "full" (default): hero size, 7 feathers + midrib veins + handle +
 *     pivot jewel. Use at ≥120px on login / marketing.
 *   - "mark": compact header/favicon size, simplified fills, no veins.
 *     Use at 28-48px.
 *
 * The SVG is kept verbatim from the design package so future design
 * updates can be diff'd cleanly against liang-design-system/assets/.
 *
 * Props:
 *   variant: "full" | "mark"   (default "full")
 *   size:    number — rendered width in px (height auto from ratio)
 *   title:   string — for aria-label; pass "" for decorative (aria-hidden)
 *   className, style: pass-through
 */

import React from 'react';

/**
 * Unique id prefix so multiple instances on one page don't collide in
 * their <defs> (Safari will bind the second instance's gradients to
 * the first instance's ids otherwise).
 */
let _uid = 0;
const nextUid = () => `lfl-${++_uid}`;

/** Full hero variant — 220x240 viewBox, 7 feathers + veins + handle. */
function FanFull({ uid }) {
  return (
    <>
      <defs>
        <radialGradient id={`${uid}-g1`} cx="110" cy="80" r="100" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#f0c870" stopOpacity="0.22" />
          <stop offset="20%" stopColor="#e8b060" stopOpacity="0.14" />
          <stop offset="45%" stopColor="#d4943c" stopOpacity="0.07" />
          <stop offset="70%" stopColor="#c47a2a" stopOpacity="0.03" />
          <stop offset="100%" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${uid}-g2`} cx="110" cy="70" r="65" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#f0c870" stopOpacity="0.2" />
          <stop offset="35%" stopColor="#e8b060" stopOpacity="0.1" />
          <stop offset="100%" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${uid}-g3`} cx="110" cy="55" r="35" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#fcd870" stopOpacity="0.22" />
          <stop offset="50%" stopColor="#f0c870" stopOpacity="0.08" />
          <stop offset="100%" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${uid}-g4`} cx="110" cy="30" r="50" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#f0c870" stopOpacity="0.14" />
          <stop offset="100%" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${uid}-fo`} x1="110" y1="170" x2="40" y2="30">
          <stop offset="0%" stopColor="#1e332b" stopOpacity="0.35" />
          <stop offset="30%" stopColor="#2d4a3e" stopOpacity="0.22" />
          <stop offset="60%" stopColor="#4a7a62" stopOpacity="0.14" />
          <stop offset="100%" stopColor="#6a9e80" stopOpacity="0.08" />
        </linearGradient>
        <linearGradient id={`${uid}-fm`} x1="110" y1="170" x2="80" y2="20">
          <stop offset="0%" stopColor="#1e332b" stopOpacity="0.3" />
          <stop offset="25%" stopColor="#2d4a3e" stopOpacity="0.2" />
          <stop offset="55%" stopColor="#4a7a62" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#6a9e80" stopOpacity="0.1" />
        </linearGradient>
        <linearGradient id={`${uid}-fc`} x1="110" y1="170" x2="110" y2="12">
          <stop offset="0%" stopColor="#1e332b" stopOpacity="0.28" />
          <stop offset="25%" stopColor="#2d4a3e" stopOpacity="0.2" />
          <stop offset="55%" stopColor="#4a7a62" stopOpacity="0.16" />
          <stop offset="80%" stopColor="#6a9e80" stopOpacity="0.1" />
          <stop offset="100%" stopColor="#86efac" stopOpacity="0.06" />
        </linearGradient>
        <linearGradient id={`${uid}-fe`} x1="110" y1="170" x2="110" y2="15">
          <stop offset="0%" stopColor="#2d4a3e" />
          <stop offset="30%" stopColor="#4a7a62" />
          <stop offset="70%" stopColor="#6a9e80" />
          <stop offset="100%" stopColor="#86efac" />
        </linearGradient>
        <linearGradient id={`${uid}-rim`} x1="110" y1="170" x2="110" y2="15">
          <stop offset="0%" stopColor="#8a6840" stopOpacity="0" />
          <stop offset="50%" stopColor="#c47a2a" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#e8b060" stopOpacity="0.25" />
        </linearGradient>
        <linearGradient id={`${uid}-hdl`} x1="110" y1="168" x2="110" y2="210">
          <stop offset="0%" stopColor="#c47a2a" />
          <stop offset="100%" stopColor="#6a4a20" />
        </linearGradient>
      </defs>
      {/* Warm amber glow layers */}
      <circle cx="110" cy="80" r="100" fill={`url(#${uid}-g1)`} />
      <circle cx="110" cy="70" r="65" fill={`url(#${uid}-g2)`} />
      <circle cx="110" cy="55" r="35" fill={`url(#${uid}-g3)`} />
      <ellipse cx="110" cy="30" rx="60" ry="30" fill={`url(#${uid}-g4)`} />
      <ellipse cx="40" cy="90" rx="25" ry="40" fill="#e8b060" fillOpacity="0.04" />
      <ellipse cx="180" cy="90" rx="25" ry="40" fill="#e8b060" fillOpacity="0.04" />
      {/* 7 green feathers, outer → center → outer */}
      <path d="M110 170 C82 152 26 118 18 78 C14 56 20 44 30 46 C40 48 54 68 70 92 C86 116 100 150 110 166" stroke={`url(#${uid}-fe)`} strokeWidth="1.2" fill={`url(#${uid}-fo)`} strokeLinejoin="round" opacity="0.65" />
      <path d="M110 170 C82 152 26 118 18 78 C14 56 20 44 30 46 C40 48 54 68 70 92 C86 116 100 150 110 166" stroke={`url(#${uid}-rim)`} strokeWidth="0.6" fill="none" opacity="0.45" />
      <path d="M110 170 C88 144 42 88 38 54 C36 34 42 24 52 28 C60 32 64 52 72 78 C82 108 98 148 110 164" stroke={`url(#${uid}-fe)`} strokeWidth="1.2" fill={`url(#${uid}-fm)`} strokeLinejoin="round" opacity="0.72" />
      <path d="M110 170 C88 144 42 88 38 54 C36 34 42 24 52 28 C60 32 64 52 72 78 C82 108 98 148 110 164" stroke={`url(#${uid}-rim)`} strokeWidth="0.6" fill="none" opacity="0.5" />
      <path d="M110 170 C98 138 66 62 68 34 C70 18 78 10 86 16 C92 22 90 46 94 74 C98 106 104 148 110 162" stroke={`url(#${uid}-fe)`} strokeWidth="1.3" fill={`url(#${uid}-fm)`} strokeLinejoin="round" opacity="0.8" />
      <path d="M110 170 C98 138 66 62 68 34 C70 18 78 10 86 16 C92 22 90 46 94 74 C98 106 104 148 110 162" stroke={`url(#${uid}-rim)`} strokeWidth="0.6" fill="none" opacity="0.55" />
      <path d="M110 170 C104 130 88 52 92 26 C94 12 102 4 110 8 C118 4 126 12 128 26 C132 52 116 130 110 170Z" stroke={`url(#${uid}-fe)`} strokeWidth="1.4" fill={`url(#${uid}-fc)`} strokeLinejoin="round" opacity="0.88" />
      <path d="M110 170 C104 130 88 52 92 26 C94 12 102 4 110 8 C118 4 126 12 128 26 C132 52 116 130 110 170Z" stroke={`url(#${uid}-rim)`} strokeWidth="0.8" fill="none" opacity="0.65" />
      <path d="M110 170 C122 138 154 62 152 34 C150 18 142 10 134 16 C128 22 130 46 126 74 C122 106 116 148 110 162" stroke={`url(#${uid}-fe)`} strokeWidth="1.3" fill={`url(#${uid}-fm)`} strokeLinejoin="round" opacity="0.75" />
      <path d="M110 170 C122 138 154 62 152 34 C150 18 142 10 134 16 C128 22 130 46 126 74 C122 106 116 148 110 162" stroke={`url(#${uid}-rim)`} strokeWidth="0.6" fill="none" opacity="0.5" />
      <path d="M110 170 C132 144 178 88 182 54 C184 34 178 24 168 28 C160 32 156 52 148 78 C138 108 122 148 110 164" stroke={`url(#${uid}-fe)`} strokeWidth="1.2" fill={`url(#${uid}-fm)`} strokeLinejoin="round" opacity="0.68" />
      <path d="M110 170 C132 144 178 88 182 54 C184 34 178 24 168 28 C160 32 156 52 148 78 C138 108 122 148 110 164" stroke={`url(#${uid}-rim)`} strokeWidth="0.6" fill="none" opacity="0.45" />
      <path d="M110 170 C138 152 194 118 202 78 C206 56 200 44 190 46 C180 48 166 68 150 92 C134 116 120 150 110 166" stroke={`url(#${uid}-fe)`} strokeWidth="1.2" fill={`url(#${uid}-fo)`} strokeLinejoin="round" opacity="0.58" />
      <path d="M110 170 C138 152 194 118 202 78 C206 56 200 44 190 46 C180 48 166 68 150 92 C134 116 120 150 110 166" stroke={`url(#${uid}-rim)`} strokeWidth="0.6" fill="none" opacity="0.4" />
      {/* Midrib veins */}
      <path d="M110 168 C86 140 32 92 30 46" stroke="#4a7a62" strokeWidth="0.4" fill="none" opacity="0.12" />
      <path d="M110 168 C90 132 46 64 52 28" stroke="#4a7a62" strokeWidth="0.45" fill="none" opacity="0.14" />
      <path d="M110 168 C100 126 70 38 86 16" stroke="#5c8c6e" strokeWidth="0.5" fill="none" opacity="0.16" />
      <path d="M110 168 L110 10" stroke="#5c8c6e" strokeWidth="0.5" fill="none" opacity="0.18" />
      <path d="M110 168 C120 126 150 38 134 16" stroke="#5c8c6e" strokeWidth="0.5" fill="none" opacity="0.16" />
      <path d="M110 168 C130 132 174 64 168 28" stroke="#4a7a62" strokeWidth="0.45" fill="none" opacity="0.14" />
      <path d="M110 168 C134 140 188 92 190 46" stroke="#4a7a62" strokeWidth="0.4" fill="none" opacity="0.12" />
      {/* Handle + pivot jewel */}
      <line x1="110" y1="168" x2="110" y2="208" stroke={`url(#${uid}-hdl)`} strokeWidth="4" strokeLinecap="round" />
      <line x1="110" y1="168" x2="110" y2="208" stroke="#4a3418" strokeWidth="1.5" strokeLinecap="round" opacity="0.12" />
      <circle cx="110" cy="168" r="6" fill="none" stroke="#c47a2a" strokeWidth="1.5" opacity="0.4" />
      <circle cx="110" cy="168" r="3" fill="#c47a2a" opacity="0.3" />
      <circle cx="110" cy="168" r="1.2" fill="#e8b060" opacity="0.55" />
    </>
  );
}

/** Compact mark — same viewBox, simplified gradients, suitable for
 *  header sizes (28–48px). Rim highlight only on center feather. */
function FanMark({ uid }) {
  return (
    <>
      <defs>
        <radialGradient id={`${uid}-mg1`} cx="110" cy="75" r="80" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#f0c870" stopOpacity="0.26" />
          <stop offset="30%" stopColor="#e8b060" stopOpacity="0.12" />
          <stop offset="100%" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${uid}-mfb`} x1="110" y1="170" x2="110" y2="10">
          <stop offset="0%" stopColor="#1e332b" stopOpacity="0.3" />
          <stop offset="60%" stopColor="#4a7a62" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#6a9e80" stopOpacity="0.08" />
        </linearGradient>
        <linearGradient id={`${uid}-meb`} x1="110" y1="170" x2="110" y2="15">
          <stop offset="0%" stopColor="#2d4a3e" />
          <stop offset="30%" stopColor="#4a7a62" />
          <stop offset="70%" stopColor="#6a9e80" />
          <stop offset="100%" stopColor="#86efac" />
        </linearGradient>
        <linearGradient id={`${uid}-mrb`} x1="110" y1="170" x2="110" y2="15">
          <stop offset="0%" stopColor="#8a6840" stopOpacity="0" />
          <stop offset="100%" stopColor="#e8b060" stopOpacity="0.28" />
        </linearGradient>
      </defs>
      <circle cx="110" cy="75" r="80" fill={`url(#${uid}-mg1)`} />
      <path d="M110 170 C82 152 26 118 18 78 C14 56 20 44 30 46 C40 48 54 68 110 166" stroke={`url(#${uid}-meb)`} strokeWidth="1.5" fill={`url(#${uid}-mfb)`} opacity="0.55" />
      <path d="M110 170 C88 144 42 88 38 54 C36 34 42 24 52 28 C60 32 66 56 110 164" stroke={`url(#${uid}-meb)`} strokeWidth="1.5" fill={`url(#${uid}-mfb)`} opacity="0.65" />
      <path d="M110 170 C98 138 66 62 68 34 C70 18 78 10 86 16 C92 22 92 50 110 162" stroke={`url(#${uid}-meb)`} strokeWidth="1.5" fill={`url(#${uid}-mfb)`} opacity="0.75" />
      <path d="M110 170 C104 130 88 52 92 26 C94 12 102 4 110 8 C118 4 126 12 128 26 C132 52 116 130 110 170Z" stroke={`url(#${uid}-meb)`} strokeWidth="2" fill={`url(#${uid}-mfb)`} opacity="0.85" />
      <path d="M110 170 C104 130 88 52 92 26 C94 12 102 4 110 8 C118 4 126 12 128 26 C132 52 116 130 110 170Z" stroke={`url(#${uid}-mrb)`} strokeWidth="1" fill="none" opacity="0.6" />
      <path d="M110 170 C122 138 154 62 152 34 C150 18 142 10 134 16 C128 22 128 50 110 162" stroke={`url(#${uid}-meb)`} strokeWidth="1.5" fill={`url(#${uid}-mfb)`} opacity="0.7" />
      <path d="M110 170 C132 144 178 88 182 54 C184 34 178 24 168 28 C160 32 154 56 110 164" stroke={`url(#${uid}-meb)`} strokeWidth="1.5" fill={`url(#${uid}-mfb)`} opacity="0.6" />
      <path d="M110 170 C138 152 194 118 202 78 C206 56 200 44 190 46 C180 48 166 68 110 166" stroke={`url(#${uid}-meb)`} strokeWidth="1.5" fill={`url(#${uid}-mfb)`} opacity="0.5" />
      <line x1="110" y1="170" x2="110" y2="210" stroke="#c47a2a" strokeWidth="6" strokeLinecap="round" opacity="0.5" />
      <circle cx="110" cy="170" r="5" fill="#c47a2a" opacity="0.3" />
      <circle cx="110" cy="170" r="2" fill="#e8b060" opacity="0.5" />
    </>
  );
}

export default function LiangFanLogo({
  variant = 'full',
  size = 180,
  title = 'Liang',
  className,
  style,
}) {
  // Generate a stable per-instance id on first render. useRef is
  // cheaper than useMemo here because we never need to recompute.
  const uidRef = React.useRef(null);
  if (uidRef.current === null) uidRef.current = nextUid();
  const uid = uidRef.current;

  // Aspect ratio locked to viewBox 220:240 = 11:12
  const height = Math.round((size * 240) / 220);
  const isDecorative = !title;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 220 240"
      width={size}
      height={height}
      fill="none"
      className={className}
      style={style}
      role={isDecorative ? 'presentation' : 'img'}
      aria-hidden={isDecorative ? true : undefined}
      aria-label={isDecorative ? undefined : title}
    >
      {!isDecorative && <title>{title}</title>}
      {variant === 'mark' ? <FanMark uid={uid} /> : <FanFull uid={uid} />}
    </svg>
  );
}
